import { generalRole, systemLabel, type AssessmentTask, type Competency, type JobInput } from '@eduvy/core';
import { useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../components/icons';
import { Badge, Button, Checkbox, Chip, Empty, ErrorNote, Field, IconButton, Input, NumberField, Options, Section, Select, Textarea, cx } from '../components/ui';
import { draft } from '../lib/drafts';

type AssessmentInput = NonNullable<JobInput['config']['assessment']>;
type DemoInput = AssessmentInput['systems'][number];
type Kind = AssessmentTask['kind'];

const KIND: Record<Kind, { label: string; tone: 'violet' | 'blue' | 'amber'; icon: IconName }> = {
  roleplay: { label: 'Role-play', tone: 'violet', icon: 'headphones' },
  written: { label: 'Written', tone: 'blue', icon: 'pen' },
  hands_on: { label: 'Hands-on', tone: 'amber', icon: 'hand' },
};

const MINUTES: Record<Kind, [number, number]> = { roleplay: [2, 10], written: [2, 15], hands_on: [3, 20] };

function newId(tasks: AssessmentTask[], base: string): string {
  let n = tasks.length + 1;
  while (tasks.some((t) => t.id === `${base}_${n}`)) n++;
  return `${base}_${n}`;
}

function toKey(label: string): string {
  const k = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return /^[a-z][a-z0-9_]{1,40}$/.test(k) ? k : `system_${k}`.slice(0, 41);
}

export interface AssessmentContext {
  title: string;
  companyName: string;
  description: string;
  competencies: Competency[];
  customerFacing: boolean;
}

/**
 * Stage 2: the skills assessment a shortlist sends. The task bank is edited here task by task, and
 * the AI can draft more tasks for the job; demo logins turn hands-on tasks on.
 */
export function AssessmentCard({ value, onChange, context, n }: { value: AssessmentInput | undefined; onChange: (a: AssessmentInput | undefined) => void; context: AssessmentContext; n?: number }) {
  if (!value) {
    return (
      <Section id="assessment" n={n} title="Skills assessment" description="Sent when you shortlist a candidate after the AI interview.">
        <Empty icon="headphones" title="No skills assessment yet" action={
          <Button
            variant="primary"
            icon="plus"
            onClick={() => {
              const keys = new Set(context.competencies.map((c) => c.key));
              const tasks = JSON.parse(JSON.stringify(generalRole.tasks)) as AssessmentTask[];
              onChange({
                extraLanguages: context.customerFacing ? 2 : 0,
                rolePlays: context.customerFacing ? 3 : 2,
                written: true,
                voiceEngine: 'standard',
                systems: [],
                tasks: tasks.map((t) => ({ ...t, skills: t.skills.filter((s) => keys.has(s)) })),
              });
            }}
          >
            Add a skills assessment
          </Button>
        }>
          Shortlisting only marks the candidate. Add one to have the AI run role-plays (it plays the people this job deals with), a written task and, with demo logins, hands-on tasks in a video meeting.
        </Empty>
      </Section>
    );
  }
  const systems = value.systems ?? [];
  const setSystems = (next: DemoInput[]) => onChange({ ...value, systems: next });
  const count = (kind: Kind) => value.tasks.filter((t) => t.kind === kind).length;

  return (
    <Section
      id="assessment"
      n={n}
      title="Skills assessment"
      description="Each candidate gets a different selection from the task bank. Role-plays run in English and, for customer-facing jobs, in other languages the candidate speaks (never their mother tongue)."
      actions={
        <Button size="sm" variant="ghost" icon="trash" onClick={() => onChange(undefined)}>
          Remove
        </Button>
      }
    >
      <div className="grid gap-5 md:grid-cols-3">
        <Field label="Role-plays per candidate" hint="At least this many; one per language, the rest in English." as="div">
          <NumberField value={value.rolePlays ?? 3} min={1} max={6} onChange={(v) => onChange({ ...value, rolePlays: v })} aria-label="Role-plays per candidate" />
        </Field>
        <Field label="Other languages" hint="Role-play languages besides English." as="div">
          <NumberField value={value.extraLanguages ?? 2} min={0} max={3} onChange={(v) => onChange({ ...value, extraLanguages: v })} aria-label="Other languages" />
        </Field>
        <Checkbox checked={value.written ?? true} onChange={(written) => onChange({ ...value, written })} hint="One typed task in the session." className="md:pt-7">
          Include a written task
        </Checkbox>
        <Field label="Voice engine for the assessment" className="md:col-span-3" as="div">
          <Options
            label="Voice engine for the assessment"
            value={value.voiceEngine ?? 'standard'}
            onChange={(v) => onChange({ ...value, voiceEngine: v as AssessmentInput['voiceEngine'] })}
            columns={2}
            options={[
              { value: 'standard', label: 'Standard', icon: 'headphones', badge: <Badge tone="green" size="xs">recommended</Badge>, hint: 'Sarvam voice and speech recognition around Gemini. Replies in about 2 s and follows the task script most strictly.' },
              { value: 'gemini-live', label: 'Gemini Live', icon: 'zap', badge: <Badge tone="amber" size="xs">experimental</Badge>, hint: 'One Google model hears and speaks: about 1 s replies and the only one that can watch a shared screen, but it sometimes stays silent and costs more.' },
            ]}
          />
        </Field>
        <Field label="Client names for {org}" optional hint="Comma-separated. Otherwise a built-in list of Indian businesses." className="md:col-span-3">
          <Input
            defaultValue={(value.orgs ?? []).join(', ')}
            placeholder="e.g. Apollo Clinics, Sharma Traders"
            onBlur={(e) => {
              const orgs = e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length >= 2)
                .slice(0, 30);
              onChange({ ...value, orgs: orgs.length ? orgs : undefined });
            }}
          />
        </Field>
      </div>

      <TaskBank value={value} onChange={onChange} context={context} systems={systems} />

      <div className="mt-8">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-fg">
            Demo systems for hands-on tasks <span className="font-mono text-xs font-normal text-muted-fg">· {count('hands_on')} hands-on tasks in the bank</span>
          </h3>
          <Button size="sm" icon="plus" disabled={systems.length >= 4} onClick={() => setSystems([...systems, { system: `system_${systems.length + 1}`, label: '', url: '', username: '', password: '' }])}>
            Add a demo system
          </Button>
        </div>
        <p className="mb-4 text-xs leading-relaxed text-muted-fg">
          The candidate shares their whole screen and works in your demo system (an ERP, a CRM, Tally…). The login is shown to them during that task only. Hands-on tasks run only for systems listed here.
        </p>
        <div className="space-y-3">
          {systems.map((d, i) => {
            const patch = (p: Partial<DemoInput>) => setSystems(systems.map((x, j) => (j === i ? { ...x, ...p } : x)));
            return (
              <ItemCard key={i} onRemove={() => setSystems(systems.filter((_, j) => j !== i))}>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Name" hint={`Hands-on tasks pick it by this name. Key: ${d.system}`}>
                    <Input
                      value={d.label ?? ''}
                      placeholder="e.g. ERP, Tally, Salesforce"
                      onChange={(e) => {
                        const label = e.target.value;
                        const key = label.trim() ? toKey(label) : d.system;
                        // Tasks that used the old key follow the rename.
                        onChange({
                          ...value,
                          systems: systems.map((x, j) => (j === i ? { ...x, label, system: key } : x)),
                          tasks: value.tasks.map((t) => (t.kind === 'hands_on' && t.system === d.system ? { ...t, system: key } : t)),
                        });
                      }}
                    />
                  </Field>
                  <Field label="Demo address" className="md:col-span-2">
                    <Input type="url" placeholder="https://" leading={<Icon name="link" className="h-4 w-4" />} value={d.url} onChange={(e) => patch({ url: e.target.value })} />
                  </Field>
                  <Field label="Username">
                    <Input value={d.username} onChange={(e) => patch({ username: e.target.value })} />
                  </Field>
                  <Field label="Password">
                    <Input value={d.password} onChange={(e) => patch({ password: e.target.value })} />
                  </Field>
                  <Field label="Notes for the candidate" optional>
                    <Input value={d.notes ?? ''} onChange={(e) => patch({ notes: e.target.value || undefined })} />
                  </Field>
                </div>
              </ItemCard>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

function TaskBank({ value, onChange, context, systems }: { value: AssessmentInput; onChange: (a: AssessmentInput) => void; context: AssessmentContext; systems: DemoInput[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [drafting, setDrafting] = useState(false);
  const tasks = value.tasks;
  const setTasks = (next: AssessmentTask[]) => onChange({ ...value, tasks: next });
  const rolePlays = tasks.filter((t) => t.kind === 'roleplay').length;
  const minutes = tasks.reduce((n, t) => n + t.minutes, 0);

  const add = (kind: Kind) => {
    const skills = context.competencies.slice(0, 1).map((c) => c.key);
    const task: AssessmentTask =
      kind === 'roleplay'
        ? { kind, id: newId(tasks, 'roleplay'), title: 'New role-play', brief: '{customer} from {org} calls you about…', persona: 'Who the AI plays, their mood and how they talk.', situation: 'The facts of the case, including a detail a good candidate uncovers by asking.', goodResolution: 'What a strong candidate does.', skills, minutes: 4 }
        : kind === 'written'
          ? { kind, id: newId(tasks, 'written'), title: 'New written task', brief: 'The situation and exactly what to write. Include every fact they need.', goodAnswer: 'What a strong answer contains.', skills, minutes: 5 }
          : { kind, id: newId(tasks, 'hands_on'), title: 'New hands-on task', system: systems[0]?.system ?? 'erp', brief: 'The steps to do in the demo system.', goodAnswer: 'What should be visible at the end.', skills, minutes: 7 };
    setTasks([...tasks, task]);
    setOpen(task.id);
  };

  return (
    <div className="mt-8">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-fg">
          Task bank <span className="font-mono text-xs font-normal text-muted-fg">· {tasks.length} tasks · {minutes} min in all</span>
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon="plus" onClick={() => add('roleplay')}>
            Role-play
          </Button>
          <Button size="sm" icon="plus" onClick={() => add('written')}>
            Written task
          </Button>
          {systems.length > 0 && (
            <Button size="sm" icon="plus" onClick={() => add('hands_on')}>
              Hands-on task
            </Button>
          )}
          <Button size="sm" variant="primary" icon="sparkles" onClick={() => setDrafting(!drafting)}>
            Draft more with AI
          </Button>
        </div>
      </div>
      <p className="mb-4 text-xs leading-relaxed text-muted-fg">
        In the texts, {'{customer}'}, {'{org}'}, {'{city}'} and {'{company}'} are filled in differently for each candidate.
      </p>

      {drafting && (
        <AiTasks
          context={context}
          tasks={tasks}
          onAdd={(added) => {
            setTasks([...tasks, ...added]);
            setFresh(new Set([...fresh, ...added.map((t) => t.id)]));
            setDrafting(false);
          }}
          onClose={() => setDrafting(false)}
        />
      )}

      <ul className="divide-y divide-border overflow-hidden rounded-md ring-1 ring-border">
        {tasks.map((t, i) => {
          const isOpen = open === t.id;
          const lastRolePlay = t.kind === 'roleplay' && rolePlays <= 1;
          const k = KIND[t.kind];
          return (
            <li key={`${t.id}-${i}`} className={cx(isOpen ? 'bg-surface-2' : 'bg-surface')}>
              <div className="flex items-center gap-3 px-3 py-2.5">
                <span className={cx('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px]', k.tone === 'violet' ? 'bg-purple/10 text-purple dark:text-accent' : k.tone === 'blue' ? 'bg-info-soft text-info' : 'bg-warning-soft text-warning')}>
                  <Icon name={k.icon} className="h-4 w-4" />
                </span>
                <button type="button" className="min-w-0 flex-1 truncate text-left text-sm font-medium text-fg hover:text-accent" onClick={() => setOpen(isOpen ? null : t.id)}>
                  {t.title}
                  {fresh.has(t.id) && (
                    <Badge tone="indigo" size="xs" className="ml-2">
                      new
                    </Badge>
                  )}
                </button>
                <Badge tone={k.tone} size="xs" className="hidden sm:inline-flex">
                  {k.label}
                </Badge>
                <span className="hidden font-mono text-xs text-muted-fg sm:inline">{t.minutes} min</span>
                <IconButton label={isOpen ? 'Close' : 'Edit'} icon={isOpen ? 'chevronUp' : 'edit'} size="sm" onClick={() => setOpen(isOpen ? null : t.id)} />
                <IconButton label="Remove" icon="trash" size="sm" disabled={lastRolePlay} title={lastRolePlay ? 'The bank needs at least one role-play' : 'Remove'} className="hover:!text-destructive" onClick={() => setTasks(tasks.filter((_, j) => j !== i))} />
              </div>
              {isOpen && (
                <div className="border-t border-border px-4 pb-5 pt-4">
                  <TaskForm task={t} competencies={context.competencies} systems={systems} onChange={(next) => setTasks(tasks.map((x, j) => (j === i ? next : x)))} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TaskForm({ task, competencies, systems, onChange }: { task: AssessmentTask; competencies: Competency[]; systems: DemoInput[]; onChange: (t: AssessmentTask) => void }) {
  const set = (patch: Partial<AssessmentTask>) => onChange({ ...task, ...patch } as AssessmentTask);
  const [lo, hi] = MINUTES[task.kind];
  return (
    <div className="grid gap-4 md:grid-cols-6">
      <Field label="Title" className="md:col-span-4">
        <Input value={task.title} onChange={(e) => set({ title: e.target.value })} />
      </Field>
      <Field label="Minutes" className="md:col-span-2" as="div">
        <NumberField value={task.minutes} min={lo} max={hi} onChange={(v) => set({ minutes: v })} aria-label="Minutes" />
      </Field>
      {task.kind === 'roleplay' && (
        <>
          <Field label="What the candidate is told" hint="Read out before it starts, in the second person." className="md:col-span-6">
            <Textarea rows={2} value={task.brief} onChange={(e) => set({ brief: e.target.value })} />
          </Field>
          <Field label="Who the AI plays" hint="Their role, mood and how they talk." className="md:col-span-3">
            <Textarea rows={3} value={task.persona} onChange={(e) => set({ persona: e.target.value })} />
          </Field>
          <Field label="Twist" optional hint="An escalation the AI brings in part-way through." className="md:col-span-3">
            <Textarea rows={3} value={task.twist ?? ''} onChange={(e) => set({ twist: e.target.value || undefined })} />
          </Field>
          <Field label="The situation" hint="Include a hidden cause or detail that a good candidate uncovers by asking." className="md:col-span-6">
            <Textarea rows={4} value={task.situation} onChange={(e) => set({ situation: e.target.value })} />
          </Field>
          <Field label="What a strong candidate does" hint="The scorer's yardstick." className="md:col-span-6">
            <Textarea rows={3} value={task.goodResolution} onChange={(e) => set({ goodResolution: e.target.value })} />
          </Field>
        </>
      )}
      {task.kind !== 'roleplay' && (
        <>
          {task.kind === 'hands_on' && (
            <Field label="Demo system" className="md:col-span-6">
              <Select value={task.system} onChange={(e) => set({ system: e.target.value })}>
                {[...new Set([task.system, ...systems.map((s) => s.system)])].map((key) => {
                  const s = systems.find((x) => x.system === key);
                  return (
                    <option key={key} value={key}>
                      {s ? systemLabel(s) : `${key.toUpperCase()} (no login added yet)`}
                    </option>
                  );
                })}
              </Select>
            </Field>
          )}
          <Field label={task.kind === 'written' ? 'The task, as the candidate sees it' : 'The steps'} hint="Include every fact, number and name they need." className="md:col-span-6">
            <Textarea rows={task.kind === 'written' ? 6 : 4} value={task.brief} onChange={(e) => set({ brief: e.target.value })} />
          </Field>
          <Field label={task.kind === 'written' ? 'What a strong answer contains' : 'What should be done at the end'} hint="The scorer's yardstick." className="md:col-span-6">
            <Textarea rows={3} value={task.goodAnswer} onChange={(e) => set({ goodAnswer: e.target.value })} />
          </Field>
        </>
      )}
      <Field label="Competencies it shows" className="md:col-span-6" as="div">
        <div className="flex flex-wrap gap-1.5">
          {competencies.map((c) => {
            const on = task.skills.includes(c.key);
            return (
              <Chip key={c.key} size="sm" on={on} onClick={() => set({ skills: on ? task.skills.filter((s) => s !== c.key) : [...task.skills, c.key].slice(0, 6) })}>
                {c.label}
              </Chip>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function AiTasks({ context, tasks, onAdd, onClose }: { context: AssessmentContext; tasks: AssessmentTask[]; onAdd: (t: AssessmentTask[]) => void; onClose: () => void }) {
  const [kind, setKind] = useState<'both' | 'roleplay' | 'written'>('both');
  const [count, setCount] = useState(3);
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { tasks: added } = await draft<{ tasks: AssessmentTask[] }>('/api/tasks/draft', {
        title: context.title,
        companyName: context.companyName,
        description: context.description.slice(0, 6000),
        competencies: context.competencies.map((c) => ({ key: c.key, label: c.label, description: c.description })),
        existing: tasks.map((t) => t.title).slice(0, 80),
        existingIds: tasks.map((t) => t.id).slice(0, 80),
        kind,
        count,
        customerFacing: context.customerFacing,
        ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
      });
      onAdd(added);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-4 rounded-md bg-accent-soft p-4 ring-1 ring-accent/30">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-accent">
        <Icon name="sparkles" className="h-4 w-4" /> Draft tasks with AI
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="What to draft">
          <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="both">Role-plays and written tasks</option>
            <option value="roleplay">Role-plays only</option>
            <option value="written">Written tasks only</option>
          </Select>
        </Field>
        <Field label="How many" as="div">
          <NumberField value={count} min={1} max={6} onChange={setCount} aria-label="How many tasks" />
        </Field>
        <Field label="Anything specific?" optional className="md:col-span-2">
          <Input value={instructions} placeholder="e.g. more angry-customer calls; a GST calculation" onChange={(e) => setInstructions(e.target.value)} />
        </Field>
      </div>
      <ErrorNote error={error} className="mt-3" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="primary" loading={busy} onClick={() => void run()}>
          {busy ? 'Drafting… (about 20 s)' : 'Draft tasks'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <span className="text-xs text-muted-fg">New tasks are added to the bank; review them before saving.</span>
      </div>
    </div>
  );
}

/** One editable item in a list (a competency, a fact, a demo system), with its remove button. */
export function ItemCard({ children, onRemove, title, className }: { children: ReactNode; onRemove?: (() => void) | undefined; title?: ReactNode; className?: string }) {
  return (
    <div className={cx('relative rounded-md bg-surface-2 p-4 ring-1 ring-border', className)}>
      {(title || onRemove) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0 text-sm font-semibold text-fg">{title}</div>
          {onRemove && <IconButton label="Remove" icon="trash" size="sm" className="-mr-1.5 -mt-1 hover:!text-destructive" onClick={onRemove} />}
        </div>
      )}
      {children}
    </div>
  );
}
